import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { message } from 'antd';
import { devApi } from '../../../services/devApi';
import type {
  ChainListItem,
  CockpitConfigResponse,
  CockpitTraceResponse,
} from '../../../services/devApi';

const AUTO_REFRESH_MS = 5000;
const DEFAULT_CHAIN_ID = 'CHAIN-03';
/** chain 總數 34 條，一次抓全量 */
const CHAIN_LIST_SIZE = 100;

export interface UseChainCockpitResult {
  // 左欄 chain 列表
  chains: ChainListItem[];
  chainsLoading: boolean;
  selectedChainId: string;
  selectChain: (chainId: string) => void;
  // 駕駛艙 config
  config: CockpitConfigResponse | null;
  configLoading: boolean;
  configError: string | null;
  // 追蹤
  docNo: string;
  setDocNo: (v: string) => void;
  trace: CockpitTraceResponse | null;
  traceLoading: boolean;
  runTrace: () => Promise<void>;
  // 自動刷新
  autoRefresh: boolean;
  setAutoRefresh: (v: boolean) => void;
  lastRefreshedAt: Date | null;
}

export function useChainCockpit(): UseChainCockpitResult {
  const [chains, setChains] = useState<ChainListItem[]>([]);
  const [chainsLoading, setChainsLoading] = useState(false);
  const [selectedChainId, setSelectedChainId] = useState<string>(DEFAULT_CHAIN_ID);

  const [config, setConfig] = useState<CockpitConfigResponse | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  const [docNo, setDocNo] = useState('');
  const [trace, setTrace] = useState<CockpitTraceResponse | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);

  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  // 自動刷新 interval 內取最新值用
  const docNoRef = useRef(docNo);
  docNoRef.current = docNo;

  // 載入 chain 列表（全量）
  useEffect(() => {
    (async () => {
      setChainsLoading(true);
      try {
        const res = await devApi.getChains({ page: 1, size: CHAIN_LIST_SIZE });
        setChains(res.data);
      } catch (err) {
        console.error('Fetch cockpit chain list error:', err);
        message.error('載入鏈條列表失敗');
      } finally {
        setChainsLoading(false);
      }
    })();
  }, []);

  // 選定 chain → 載入 cockpit config，重置追蹤狀態
  useEffect(() => {
    let cancelled = false;
    setConfig(null);
    setConfigError(null);
    setTrace(null);
    setDocNo('');
    setAutoRefresh(false);
    setLastRefreshedAt(null);
    setConfigLoading(true);
    (async () => {
      try {
        const data = await devApi.getCockpitConfig(selectedChainId);
        if (!cancelled) setConfig(data);
      } catch (err) {
        if (cancelled) return;
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setConfigError(`找不到鏈條 ${selectedChainId}`);
        } else {
          console.error('Fetch cockpit config error:', err);
          setConfigError('載入駕駛艙設定失敗');
        }
      } finally {
        if (!cancelled) setConfigLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedChainId]);

  const selectChain = useCallback((chainId: string) => {
    setSelectedChainId(chainId);
  }, []);

  // 執行追蹤（silent = 自動刷新時不彈錯誤訊息）
  const doTrace = useCallback(
    async (silent: boolean) => {
      const target = docNoRef.current.trim();
      if (!target) {
        if (!silent) message.warning('請先輸入測試單號');
        return;
      }
      if (!silent) setTraceLoading(true);
      try {
        const data = await devApi.getCockpitTrace(selectedChainId, target);
        setTrace(data);
        setLastRefreshedAt(new Date());
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 409) {
          if (!silent) message.warning('此鏈尚未設定追蹤映射');
          setAutoRefresh(false);
        } else {
          console.error('Cockpit trace error:', err);
          if (!silent) message.error('追蹤查詢失敗');
        }
      } finally {
        if (!silent) setTraceLoading(false);
      }
    },
    [selectedChainId]
  );

  const runTrace = useCallback(async () => {
    await doTrace(false);
  }, [doTrace]);

  // 自動刷新：開啟時每 5 秒重打 trace API
  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(() => {
      doTrace(true);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [autoRefresh, doTrace]);

  return {
    chains,
    chainsLoading,
    selectedChainId,
    selectChain,
    config,
    configLoading,
    configError,
    docNo,
    setDocNo,
    trace,
    traceLoading,
    runTrace,
    autoRefresh,
    setAutoRefresh,
    lastRefreshedAt,
  };
}
